import playerPlaceholder from "assets/images/default-player.jpg";
import { useConstants } from "features/constantsProvider/context";
import { useDebounce } from "../../hooks/apiManagement/useDebounce";
import client from "libs/client";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { SingleValue } from "react-select";
import { useToast } from "ui-kit";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { ValidationStatus, type ExistenceValidationResult } from "./existenceValidation";

interface ContentCellProps {
    id?: number | string;
    title: string;
    content: string | number | boolean;
    boolean?: boolean;
    booleanValue?: boolean;
    date?: boolean;
    number?: boolean;
    phoneNumber?: boolean;
    field?: string;
    patchMethod?: "signup" | "user" | "profile";
    isImage?: boolean;
    selectItems?: {
        label: string;
        value: string | number;
    }[];
    /** Optional async existence check run (debounced) while editing; blocks submit on an invalid value. */
    validate?: (value: string) => Promise<ExistenceValidationResult>;
}

type ValidationState = { status: ValidationStatus; label?: string; message?: string };

export function ContentCell({ id, field, title, content, boolean, booleanValue, date, phoneNumber, number, selectItems, patchMethod, isImage, validate }: ContentCellProps) {
    const [edit, setEdit] = useState(false);

    const formatDate = (date: string) => {
        if (date.includes('/')) {
            const parts = date.split('/');
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return date;
    }

    const [inputValue, setInputValue] = useState(
        typeof content !== "boolean" ? (date && typeof content === "string" ? formatDate(content) : content) : ""
    )
    const { addSuccessToast, addErrorToast } = useToast();
    const inputRef = useRef<HTMLInputElement>(null);
    const { apiURL } = useConstants();

    const [validation, setValidation] = useState<ValidationState>({ status: ValidationStatus.Idle });
    const validationReqId = useRef(0);
    const debouncedValue = useDebounce(inputValue, 400);
    const submitDisabled = validation.status === ValidationStatus.Checking || validation.status === ValidationStatus.Invalid;

    useEffect(() => {
        if (!validate || !edit) {
            setValidation({ status: ValidationStatus.Idle });
            return;
        }
        const value = typeof debouncedValue === "string" ? debouncedValue : String(debouncedValue ?? "");
        // Nothing to check for an empty value; otherwise validate (incl. the current
        // value) so the resolved name is shown as soon as the field is opened.
        if (value === "") {
            setValidation({ status: ValidationStatus.Idle });
            return;
        }
        const reqId = ++validationReqId.current;
        setValidation({ status: ValidationStatus.Checking });
        validate(value)
            .then((result) => {
                if (reqId !== validationReqId.current) return;
                setValidation({ status: result.outcome, label: result.label, message: result.message });
            })
            .catch(() => {
                if (reqId !== validationReqId.current) return;
                setValidation({ status: ValidationStatus.Unverified, message: "Could not verify — will be checked on save" });
            });
    }, [debouncedValue, edit, validate, content]);

    const getInitSelectValue = (value: string | number) => {
        return selectItems?.find((item) => item.value === value);
    }

    const [selectValue, setSelectValue] = useState(selectItems && typeof content !== "boolean" ? getInitSelectValue(content) : undefined)

    const signupSubmit = client.patchSignUpRequest.useMutation({
        onSuccess: () => {
            setEdit(false)
            client.queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("playerUserDetail"), })
            addSuccessToast("Field updated")
        },
        onError: (error) => {
            addErrorToast(error.message)
        }
    });

    const userSubmit = client.patchPlayerUser.useMutation({
        onSuccess: () => {
            setEdit(false)
            client.queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("playerUserDetail"), })
            addSuccessToast("Field updated")
        },
        onError: (error) => {
            addErrorToast(error.message)
        }
    });

    const profileSubmit = client.patchPlayerProfile.useMutation({
        onSuccess: () => {
            setEdit(false)
            client.queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("playerUserDetail"), })
            addSuccessToast("Field updated")
        },
        onError: (error) => {
            addErrorToast(error.message)
        }
    });

    const onSubmit = () => {
        if (submitDisabled) return;
        const newValue = selectItems ? selectValue?.value : inputValue
        if (field) {
            if (patchMethod === "signup") {
                signupSubmit.mutate({ id: String(id) ?? "", body: { [field]: newValue ? newValue : null, id: String(id) ?? "" } })
            }
            if (patchMethod === "user") {
                userSubmit.mutate({ id: Number(id) ?? "", body: { [field]: newValue ? newValue : null, id: Number(id) ?? "" } })
            }
            if (patchMethod === "profile") {
                profileSubmit.mutate({ id: Number(id) ?? "", body: { [field]: newValue ? newValue : null, id: Number(id) ?? "" } })
            }
        }
    }

    useEffect(() => {
        if (edit && inputRef.current) {
            inputRef.current.focus();
        }
    }, [edit]);

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            onSubmit()
        }
        if (e.key === "Escape") {
            setEdit(false)
            setInputValue(typeof content !== "boolean" ? content : "")
        }
    }

    const onInputChange = (value: string) => {
        const newPhoneValue = value.replace(/[^0-9+]/g, '');
        const newNumericValue = value.replace(/[^0-9]/g, '');

        if (phoneNumber) {
            setInputValue(newPhoneValue);
        }
        else if (number) {
            setInputValue(newNumericValue);
        }
        else {
            setInputValue(value);
        }
    }

    const onSelectChange = (value: SingleValue<{ label: string; value: string | number; }>) => {
        setSelectValue((value ? value : undefined))
    }

    const handleCancel = () => {
        if (selectItems && typeof content !== "boolean") {
            setSelectValue(getInitSelectValue(content));
        } else {
            setInputValue(typeof content !== "boolean" ? content : "");
        }
        setEdit(false);
    };

    const renderEditableContent = () => {
        if (edit && !selectItems) {
            return (
                <input
                    ref={inputRef}
                    className="cellInput"
                    type={date ? "date" : "text"}
                    onKeyDown={onKeyDown}
                    value={inputValue}
                    onChange={(e) => onInputChange(e.target.value)}
                />
            );
        } if (edit && selectItems && typeof content !== "boolean") {
            return (
                <SelectComponent
                    isCleareable
                    value={selectValue}
                    handleChange={onSelectChange}
                    items={selectItems}
                />
            );
        }
        return null;
    };

    const renderContent = () => {
        if (boolean) {
            return (
                <div className={booleanValue ? "true" : "false"}>
                    {content}
                </div>
            );
        }
        if (isImage) {
            if (typeof content === "string") {
                return (
                    <div className="cellImage-container">
                        <img src={content.includes("http") ? content : `${apiURL}/${content}`} alt={`${title}`} className="cellImage" />
                        <Link to={content.includes("http") ? content : `${apiURL}/${content}`}>Photo Link</Link>
                    </div>
                );
            }
            return (
                <div className="cellImage-container" >
                    <img src={playerPlaceholder} alt={`${title}`} className="cellImage" />
                </div >
            )
        }
        if (!(edit || boolean || selectItems)) {
            return content;
        } if (!edit && selectItems && typeof content !== "boolean") {
            return getInitSelectValue(content)?.label ?? "-";
        }
        return null;
    };

    return (
        <div className="detailCell">
            <div className="cellTitle">
                {title}: {field && !edit && (
                    <span onClick={() => setEdit(true)} className="icon-new icon-crayon icon-grey" />
                )}
            </div>
            <div className="cellContent">
                {renderContent()}
                {renderEditableContent()}
                {validate && edit && validation.status !== ValidationStatus.Idle && (
                    <div className={`cellValidation cellValidation--${validation.status}`}>
                        {validation.status === ValidationStatus.Checking && "Checking…"}
                        {validation.status === ValidationStatus.Valid && `✓ ${validation.label ?? "Found"}`}
                        {validation.status === ValidationStatus.Invalid && `✗ ${validation.message}`}
                        {validation.status === ValidationStatus.Unverified && validation.message}
                    </div>
                )}
            </div>
            {edit && (
                <div className="buttons">
                    <button className="submitButton" type="button" disabled={submitDisabled} onClick={onSubmit}>Submit</button>
                    <button className="cancelButton" type="button" onClick={handleCancel}>Cancel</button>
                </div>
            )}
        </div>
    );
}
